// AI-CONTEXT: See docs/features/planner-overzicht-bkb.md
// De lijst 'Inplannen' in het huishoudenoverzicht is alles wat ingepland moet
// worden, niet alleen de status 'Inplannen' (verzoek BuurtKlusBedrijf
// 2026-10-06): een vervolgopdracht waar nog geen afspraak voor staat hoort er
// ook in, anders ziet de planner hem over het hoofd. De status zelf verandert
// niet — die is de trechter en stuurt de tabs en de grafieken; de kolom STATUS
// laat zien waarom een rij hier staat. Eén regel voor de lijst én de teller op
// de tab, zodat die twee niet uiteen kunnen lopen. Het label 'Opnieuw
// inplannen' (gezet door een klusser die zelf niet mag inplannen) telt ook
// mee, welke status het huishouden ook heeft.

import { REPLAN_LABEL_KEY, hasLabel } from './resolveAdressLabels'

/**
 * Waarom een huishouden in de lijst 'Inplannen' staat.
 *
 * `plannedIds` zijn de huishouden-ID's die al een toekomstige afspraak hebben
 * (uit `nextVisitPerAdress`); `null`/`undefined` betekent 'de afspraken zijn
 * nog niet geladen'. Dan telt een vervolgopdracht NIET mee: we weten niet of
 * er al een afspraak staat, en 'nog niet bekend' valt dicht — anders flitst
 * elke vervolgopdracht even door de lijst.
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * Een Set van ID's en niet de map zelf: de pagina leidt er een stabiele sleutel
 * uit af, zodat een nieuwe map-referentie bij elke snapshot de lijst niet
 * opnieuw laat ontstaan — dat zette de export-terugkoppeling in een lus.
 *
 * @param {{ID?: string, Status?: string}} item - Het huishouden
 * @param {Set<string>|null|undefined} plannedIds - Huishoudens mét toekomstige afspraak, of null zolang de afspraken laden
 * @returns {'status'|'label'|'follow-up'|null} De reden, of null als het huishouden er niet in hoort
 */
const planningReason = (item, plannedIds) => {
    if (item?.Status === 'planning') return 'status'
    // Beide 'zolang er geen afspraak staat': met een afspraak is het verzoek
    // beantwoord — het label gaat dan ook vanzelf weg (clearReplanLabel).
    if (!plannedIds || plannedIds.has(item?.ID)) return null
    if (hasLabel(item, REPLAN_LABEL_KEY)) return 'label'
    if (item?.Status === 'follow-up') return 'follow-up'
    return null
}

/**
 * @param {Array<object>} [items]
 * @param {Set<string>|null|undefined} plannedIds
 * @returns {Array<object>} De huishoudens voor de lijst 'Inplannen', in de aangeleverde volgorde
 */
const planningQueue = (items, plannedIds) =>
    (Array.isArray(items) ? items : []).filter((item) => planningReason(item, plannedIds) !== null)

/**
 * De teller op de tab 'Inplannen': dezelfde regel als de lijst.
 * @param {Array<object>} [items]
 * @param {Set<string>|null|undefined} plannedIds
 * @returns {number}
 */
const planningQueueCount = (items, plannedIds) => planningQueue(items, plannedIds).length

export { planningReason, planningQueueCount }
export default planningQueue
